
import React from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChatListItem as ChatListItemType } from "@/types/chat";
import { formatRelativeTime } from "@/utils/chatFormatters";
import { truncateWithHighlight, highlightSearchTerm } from "@/utils/searchHighlight";
import { SafeHighlight } from "@/components/ui/SafeHighlight";
import { useImageUrl } from '@/hooks/useImageUrl';
import { debugLogger } from '@/utils/debugLogger';
import { ChatItemContextMenu } from './ChatItemContextMenu';
import { DeletedChatIndicator } from './DeletedChatIndicator';
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

interface ChatListItemProps {
  chat: ChatListItemType;
  onDelete?: (conversationId: string) => void;
  onRestore?: (conversationId: string) => void;
  variant?: 'standalone' | 'sidebar';
}

export const ChatListItem: React.FC<ChatListItemProps> = ({ chat, onDelete, onRestore, variant = 'standalone' }) => {
  const isMobile = useIsMobile();
  const hasUnread = chat.unread > 0;
  const partnerName = chat.partner.username || 'Usuário';
  const partnerInitial = partnerName?.[0] || '?';
  const isOnline = chat.partner.isOnline || false;
  const isDeleted = chat.isDeleted || false;
  const canRestore = isDeleted && chat.deletedByCurrentUser;
  
  // Use async hook to regenerate signed URLs for avatars
  const { url: partnerAvatar } = useImageUrl(chat.partner.avatar, 'avatar');
  
  const displayMessage = React.useMemo(() => {
    // Se a conversa foi deletada, não mostrar mensagem
    if (isDeleted) {
      return null;
    }

    // Se a última mensagem foi especificamente marcada como apagada
    if (chat.lastMessage === 'Mensagem apagada') {
      return {parts: [{text: 'Mensagem apagada', highlighted: false}], needsHighlight: false};
    }

    // Se não há mensagem na conversa (conversa nova)
    if (chat.lastMessage === null || chat.lastMessage === '') {
      return {parts: [{text: 'Envie a primeira mensagem!', highlighted: false}], needsHighlight: false};
    }

    if (chat.isSearchResult && chat.searchTerm) {
      if (chat.matchType === 'message' && chat.lastMessage) {
        // For message matches, show the matched message with highlighting and responsive truncation
        const maxLength = isMobile ? 45 : 90; // Shorter on mobile to prioritize search term
        return truncateWithHighlight(chat.lastMessage, chat.searchTerm, maxLength, isMobile);
      } else if (chat.matchType === 'username') {
        // For username matches, show regular last message or placeholder
        return {parts: [{text: chat.lastMessage || 'Envie a primeira mensagem!', highlighted: false}], needsHighlight: false};
      }
    }
    // For regular conversations, show the last message normally
    return {parts: [{text: chat.lastMessage || 'Envie a primeira mensagem!', highlighted: false}], needsHighlight: false};
  }, [chat.isSearchResult, chat.searchTerm, chat.lastMessage, chat.matchType, isDeleted, isMobile]);

  const displayUsername = React.useMemo(() => {
    if (chat.isSearchResult && chat.searchTerm && chat.matchType === 'username') {
      return highlightSearchTerm(partnerName, chat.searchTerm);
    }
    return [{text: partnerName, highlighted: false}];
  }, [chat.isSearchResult, chat.searchTerm, chat.matchType, partnerName]);

  const handleDelete = () => {
    if (onDelete) {
      onDelete(chat.id);
    }
  };

  const handleRestore = () => {
    if (onRestore) {
      onRestore(chat.id);
    }
  };
  
  debugLogger.debug('[ChatListItem] Rendering chat item:', {
    chatId: chat.id,
    partnerName,
    partnerUsername: chat.partner.username,
    partnerAvatar: chat.partner.avatar,
    processedAvatar: partnerAvatar,
    isOnline,
    isSearchResult: chat.isSearchResult,
    matchType: chat.matchType,
    isDeleted,
    deletedByCurrentUser: chat.deletedByCurrentUser,
    deletedByPartner: chat.deletedByPartner,
    canRestore,
    displayMessage: displayMessage?.parts?.[0]?.text?.slice(0, 30) + '...' || 'No message'
  });

  const chatContent = (
    <div className={`flex items-center rounded-lg transition-colors ${
      variant === 'standalone' ? 'p-4' : 'p-3'
    } ${
      isDeleted 
        ? 'opacity-60 hover:bg-white/5' // Estilo para conversas deletadas mas clicáveis
        : `hover:bg-white/5 ${hasUnread ? 'bg-white/5' : ''}`
    }`}>
      <div className="relative">
        <Avatar className={`ring-2 ${
          variant === 'standalone' ? 'h-16 w-16' : 'h-12 w-12'
        } ${
          isDeleted ? 'ring-white/5' : 'ring-white/10'
        }`}>
          <AvatarImage 
            src={partnerAvatar || ''} 
            alt={partnerName}
            className={`object-cover w-full h-full ${
              isDeleted ? 'grayscale opacity-60' : ''
            }`}
          />
          <AvatarFallback className={`text-xl ${
            isDeleted 
              ? 'bg-luxury-purple/10 text-white/40' 
              : 'bg-luxury-purple/20 text-white'
          }`}>
            {partnerInitial}
          </AvatarFallback>
        </Avatar>
        
        {!isDeleted && isOnline && (
          <div className={`absolute bottom-0 right-0 bg-green-500 border-2 border-white rounded-full z-20 ${
            variant === 'standalone' ? 'w-4 h-4' : 'w-3 h-3'
          }`}></div>
        )}
        
        {!isDeleted && hasUnread && (
          <div className={`absolute -top-1 -right-1 bg-luxury-red text-white rounded-full flex items-center justify-center font-medium z-20 ${
            variant === 'standalone' ? 'text-xs w-5 h-5' : 'text-xs w-4 h-4'
          }`}>
            {chat.unread > 9 ? '9+' : chat.unread}
          </div>
        )}
      </div>
      <div className={variant === 'standalone' ? 'ml-4 flex-1 min-w-0' : 'ml-3 flex-1 min-w-0'}>
        <div className="flex justify-between items-center">
          <h3 
            className={`font-semibold truncate ${
              variant === 'standalone' ? 'text-base' : 'text-sm'
            } ${
              isDeleted 
                ? 'text-white/40' 
                : hasUnread ? 'text-luxury-red' : 'text-white'
            }`}
          >
            <SafeHighlight parts={displayUsername} />
          </h3>
          {/* Só mostrar tempo se não for deletada */}
          {!isDeleted && (
            <span className={`text-white/50 ml-2 flex-shrink-0 relative z-20 ${
              variant === 'standalone' ? 'text-xs' : 'text-xs'
            }`}>
              {/* Usar horário da mensagem específica quando for resultado de busca por mensagem */}
              {chat.isSearchResult && chat.matchType === 'message' && (chat as any).messageCreatedAt
                ? formatRelativeTime((chat as any).messageCreatedAt)
                : chat.time ? formatRelativeTime(chat.time) : ''}
            </span>
          )}
        </div>
        
        {/* Mostrar indicador especial para conversas deletadas */}
        {isDeleted ? (
          <DeletedChatIndicator 
            partnerName={partnerName}
            deletedByCurrentUser={chat.deletedByCurrentUser}
            deletedByPartner={chat.deletedByPartner}
          />
        ) : (
          displayMessage && displayMessage.needsHighlight ? (
            <p 
              className={`line-clamp-1 ${
                variant === 'standalone' ? 'text-sm' : 'text-xs'
              } ${hasUnread ? 'text-white' : 'text-white/70'}`}
            >
              <SafeHighlight parts={displayMessage.parts} />
            </p>
          ) : (
            <p 
              className={`line-clamp-1 ${
                variant === 'standalone' ? 'text-sm' : 'text-xs'
              } ${hasUnread ? 'text-white' : 'text-white/70'}`}
            >
              {displayMessage?.parts?.[0]?.text || 'Nova mensagem'}
            </p>
          )
        )}
      </div>
    </div>
  );
  
  // Todas as conversas (deletadas ou não) podem ser clicadas
  return (
    <div>
      <ChatItemContextMenu 
        onDelete={handleDelete}
        onRestore={handleRestore}
        isDeleted={isDeleted}
        canRestore={canRestore}
      >
        <Link
          key={chat.id}
          to={`/main/chat/${chat.partner.username || chat.partner.id}`}
          className="block"
        >
          {chatContent}
        </Link>
      </ChatItemContextMenu>
      <Separator className="opacity-10" />
    </div>
  );
};
